import { Cause, Effect, Exit, Layer, Ref, Result, Schema } from "effect";
import { StationId } from "@workerstation/shared/station-id";
import { stationInvalid } from "@workerstation/shared/errors/station";
import { AcceptedWork } from "../accepted-work.ts";
import type { StationClientFailure } from "../../api/station/model.ts";
import { StationApi } from "../../api/station/service.ts";
import { PersistenceOrder } from "./persistence-order.ts";
import { CurrentSession, SessionCandidate, SessionMembers, sessionPersistenceKey } from "./session-model.ts";
import { StationSession } from "./session-service.ts";

const identity = (stationId: string) => Schema.decodeUnknownEffect(StationId)(stationId).pipe(
  Effect.mapError(() => stationInvalid("Invalid station persistence target.")));
const normalize = Effect.fn("Frontend.StationSession.normalize")(function* (input: unknown) {
  const value = yield* Schema.decodeUnknownEffect(SessionCandidate)(input).pipe(
    Effect.mapError(() => stationInvalid("Invalid station membership state.")));
  const first = value.workspaces[0];
  if (first === undefined) return yield* Effect.fail(stationInvalid("Station membership cannot be empty."));
  return { workspaces: value.workspaces,
    active: value.workspaces.find(id => id === value.active) ?? first, updatedAt: value.updatedAt } satisfies CurrentSession;
});

/** Root resources outlive view queries and join every accepted finite mutation. */
export const makeStationSession = Effect.fn("Frontend.StationSession.make")(function* () {
  const api = yield* StationApi, accepted = yield* AcceptedWork, order = yield* PersistenceOrder;
  const privateFailures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  yield* Effect.addFinalizer(() => Ref.get(privateFailures).pipe(Effect.flatMap(reasons =>
    reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const commit = <A>(stationId: string, operation: Effect.Effect<A, StationClientFailure>) => Effect.gen(function* () {
    const exit = yield* accepted.join(Effect.gen(function* () {
      const result = yield* Effect.exit(order.run(sessionPersistenceKey(stationId), operation));
      if (Exit.isFailure(result)) yield* Ref.update(privateFailures, current =>
        [...current, ...result.cause.reasons.filter(reason => reason._tag === "Die")]);
      return result;
    }));
    return yield* exit;
  });
  return StationSession.of({
    persist: Effect.fn("Frontend.StationSession.persist")(function* (stationId, input) {
      const target = yield* identity(stationId);
      const members = yield* Schema.decodeUnknownEffect(SessionMembers)(input).pipe(
        Effect.mapError(() => stationInvalid("Invalid station membership update.")));
      return yield* commit(target, api.updateStation(target, members));
    }),
    mutate: Effect.fn("Frontend.StationSession.mutate")(function* (stationId, change) {
      const target = yield* identity(stationId);
      const attempt = (remaining: number): Effect.Effect<CurrentSession, StationClientFailure> => Effect.gen(function* () {
        const current = yield* normalize(yield* api.station(target));
        // Keep the comparison snapshot independent of a callback's local array.
        const proposed = yield* Effect.suspend(() => change({ ...current, workspaces: [...current.workspaces] }));
        const next = yield* normalize(proposed);
        if (next.active === current.active && next.workspaces.length === current.workspaces.length
          && next.workspaces.every((id, index) => id === current.workspaces[index])) return next;
        const result = yield* Effect.result(api.updateStation(target, {
          workspaces: next.workspaces, active: next.active, expectedUpdatedAt: current.updatedAt
        }));
        if (Result.isSuccess(result)) return next;
        const failure = result.failure;
        return failure._tag === "StationHttpError" && failure.status === 409 && remaining > 1
          ? yield* attempt(remaining - 1) : yield* Effect.fail(failure);
      });
      return yield* commit(target, attempt(3));
    })
  });
});
export const StationSessionLive = Layer.effect(StationSession, makeStationSession());
